import {useMemo} from 'react'

import {ThemeProvider} from '@primer/react-brand/esm'

import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {FootnotesProvider} from '@github-ui/swp-core/components/contentful/FootnotesContext'
import {useContentfulData} from '@github-ui/swp-core/lib/hooks/use-contentful-data'

import {VendorPixelTracker} from '../../../components/VendorPixelTracker'
import {toPayload} from '../../../lib/types/payload'

import {parseBrandPage, type BrandPageContent as ParsedBrandPageContent, type BrandPageDefinition} from './definition'

type Props<Definition extends BrandPageDefinition> = {
  children: (content: ParsedBrandPageContent<Definition>) => React.ReactNode
  className?: string
  colorMode?: 'light' | 'dark' | 'auto'
  definition: Definition
  footnotesPlacement?: 'inside' | 'outside'
  withFootnotes?: boolean
  wrapTheme?: (children: React.ReactNode) => React.ReactNode
}

/**
 * Owns vendor pixel tracking for the page. Remove any route-level VendorPixelTracker
 * when migrating a route to avoid firing the same pixels twice.
 */
export function BrandPageContent<Definition extends BrandPageDefinition>(props: Props<Definition>) {
  const {
    children,
    className,
    colorMode,
    definition,
    footnotesPlacement = 'outside',
    withFootnotes = false,
    wrapTheme,
  } = props
  const routePayload = useRoutePayload<unknown>()
  const {contentfulRawJsonResponse, vendorPixels} = useMemo(() => toPayload(routePayload), [routePayload])
  const contentfulResponse = useContentfulData(contentfulRawJsonResponse)
  const {colorMode: pageColorMode, content} = useMemo(
    () => parseBrandPage(contentfulResponse, definition),
    [contentfulResponse, definition],
  )

  const pageContent = children(content)
  const themeContent =
    withFootnotes && footnotesPlacement === 'inside' ? (
      <FootnotesProvider>{pageContent}</FootnotesProvider>
    ) : (
      pageContent
    )
  const theme = (
    <ThemeProvider colorMode={colorMode ?? pageColorMode} className={className}>
      {themeContent}
    </ThemeProvider>
  )
  const wrappedTheme = wrapTheme ? wrapTheme(theme) : theme

  const page =
    withFootnotes && footnotesPlacement === 'outside' ? (
      <FootnotesProvider>{wrappedTheme}</FootnotesProvider>
    ) : (
      wrappedTheme
    )

  return <VendorPixelTracker vendorPixels={vendorPixels ?? []}>{page}</VendorPixelTracker>
}
